/**
 * correlationId context store.
 *
 * Exports:
 *   generateCorrelationId() — returns a new ULID string
 *   runWithContext(ctx, fn) — runs fn with ctx as the active context
 *   getContext()            — returns the active context, or {} if none
 *
 * Implementation: closure stack. Correct for sequential async code (browser
 * and Vitest tests). Cloud Functions use withObservability (Step 3) which
 * wraps this with Node's AsyncLocalStorage for true per-request isolation.
 *
 * Architecture rule: AsyncLocalStorage is the canonical mechanism for
 * concurrent Node processes. The stack here is the portable layer that
 * logger.js reads from, and it delegates to ALS when available (Step 3
 * wires that up via the withObservability wrapper).
 */

// ── ULID generation ───────────────────────────────────────────────────────────
// Inline implementation — no external dep. Produces 26-char Crockford base32.
// Time component: 48-bit ms timestamp → 10 chars. Random: 80-bit → 16 chars.

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export function generateCorrelationId() {
  let t = Date.now();
  const tc = new Array(10);
  for (let i = 9; i >= 0; i--) {
    tc[i] = CROCKFORD[t % 32];
    t = Math.floor(t / 32);
  }

  const rnd = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(rnd);
  } else {
    rnd.forEach((_, i, a) => { a[i] = Math.floor(Math.random() * 256); });
  }
  // 256 = 8 × 32, so b & 0x1f is uniformly distributed
  const rc = Array.from(rnd, b => CROCKFORD[b & 0x1f]);

  return tc.join('') + rc.join('');
}

// ── Context stack ─────────────────────────────────────────────────────────────
// Each runWithContext call pushes a context object; getContext() reads the top.
// For concurrent Node processes, Step 3's withObservability layers AsyncLocalStorage
// on top of this via an ALS-backed getContext override in functions/src/obs/context.js.

const _stack = [];

/** Returns the active context, or {} if called outside runWithContext. */
export function getContext() {
  return _stack.length > 0 ? _stack[_stack.length - 1] : {};
}

/**
 * Runs fn() with ctx as the active context.
 * Handles both synchronous and async (Promise-returning) fn.
 * Context is popped after fn completes (or rejects).
 */
export function runWithContext(ctx, fn) {
  _stack.push(ctx);
  let result;
  try {
    result = fn();
  } catch (e) {
    _stack.pop();
    throw e;
  }
  if (result !== null && typeof result === 'object' && typeof result.then === 'function') {
    return result.then(
      v => { _stack.pop(); return v; },
      e => { _stack.pop(); throw e; },
    );
  }
  _stack.pop();
  return result;
}

